import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import useMobileView from '../../../../../hooks/useMobileView';
import { ImageComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { createDisplayLink } from '../../../../../services/helper';
import EmptyComponent from '../../../pageBuilder/EmptyComponent';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';

const settings = {
  dots: false,
  infinite: true,
  speed: 650,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const ImageUiComponent = ({ uiComponent }: UiComponentElementInterface<ImageComponentInterface>) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const ref = useRef<Slider | null>(null);
  const { t } = useTranslation();
  const { getMobileViewWidth } = useMobileView();

  function changedSlide(_: number, newIndex: number) {
    setActiveSlide(newIndex);
  }

  function calculateAspectPadding() {
    if (uiComponent?.customStyling?.height && uiComponent?.customStyling?.width && !uiComponent?.customStyling?.aspectRatio) {
      return (uiComponent?.customStyling?.height / uiComponent?.customStyling?.width) * 100;
    } else if (uiComponent?.customStyling?.aspectRatio && uiComponent?.customStyling?.aspectRatio.x && uiComponent?.customStyling?.aspectRatio.y) {
      return (uiComponent.customStyling.aspectRatio?.y / uiComponent.customStyling.aspectRatio.x) * 100;
    } else return 25;
  }

  function getImageWidth() {
    if (uiComponent?.customStyling.width) {
      return `${uiComponent?.customStyling.width}px`;
    }
    return '100%';
  }

  useEffect(() => {
    if (uiComponent?.autoplay) ref.current?.slickPlay();
  }, [uiComponent]);

  const galleryElement = () => {
    if (!uiComponent?.images) return <></>;

    // https://github.com/akiran/react-slick/issues/2387
    const sliderSettings = {
      ...settings,
      infinite: uiComponent.images.length > 1,
    };

    return (
      <>
        <div className="relative overflow-hidden gallery-slider" style={{ width: getMobileViewWidth() ?? '100%' }}>
          <Slider beforeChange={changedSlide} {...sliderSettings} autoplay={uiComponent.autoplay} ref={ref} arrows={uiComponent.controls}>
            {uiComponent.images.map((slide) => (
              <div key={slide}>
                <div
                  className="relative w-full"
                  style={{
                    paddingBottom: `${calculateAspectPadding()}%`,
                    height: 0,
                  }}>
                  <div
                    className="w-full"
                    style={{
                      background: `url(${createDisplayLink(slide, false, undefined, undefined)})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      height: '100%',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                  />
                </div>
              </div>
            ))}
          </Slider>
          {uiComponent.images.length > 1 && uiComponent.controls && (
            <ul className="absolute bottom-0 flex w-full gap-2 px-12 mb-3">
              {uiComponent.images.map((slide, index) => (
                <li className={`w-full rounded-md bg-white h-1 ${index === activeSlide ? 'opacity-100' : 'opacity-50'}`} key={slide} />
              ))}
            </ul>
          )}
        </div>
      </>
    );
  };
  return (
    <>
      <TitleAndDescription component={uiComponent!} />
      {uiComponent && uiComponent.imageComponentType === 'slideshow' && uiComponent?.images && uiComponent?.images.length > 0 ? (
        <div className="max-w-[100cqw] overflow-hidden h-full justify-center flex" style={{ color: uiComponent?.styling?.textColor || '#000' }}>
          {galleryElement()}
        </div>
      ) : uiComponent && uiComponent.imageComponentType === 'image' && (uiComponent.imageFile || uiComponent.imageLink) ? (
        <div className="w-full">
          <img width="100%" height="auto" src={uiComponent?.imageType === 'file' ? createDisplayLink(uiComponent?.imageFile, false) : uiComponent?.imageLink} />
        </div>
      ) : (
        <div className="flex justify-center">
          <EmptyComponent componentName={t('pagebuilder:image')} />
        </div>
      )}
    </>
  );
};

export default ImageUiComponent;
